import type {
  CatalogueItemRouteHint,
  CatalogueItemTharsternProvenance,
} from "../data/catalogue-types";
import { formatDateOrDash } from "./formatters";

// The words staff read on a migrated catalogue item's page (v1 completion F,
// spec 4.3): where the item's last job went, and which Tharstern jobs it was
// made from. Staff only; a customer never sees a supplier or a press.

/** "bought from Solopress (job 738048, 2 Sep 2026)" / "made in-house, ROLAND 700 (job ..., ...)". */
export function lastTimeText(hint: CatalogueItemRouteHint): string {
  const who =
    hint.route === "buy"
      ? `bought from ${hint.supplierName ?? "a supplier"}`
      : hint.costCentreCode
        ? `made in-house, ${hint.costCentreCode}`
        : "made in-house";
  const jobParts = [
    hint.lastJobNo ? `job ${hint.lastJobNo}` : null,
    hint.lastOn ? formatDateOrDash(hint.lastOn) : null,
  ].filter((part): part is string => part !== null);
  return jobParts.length > 0 ? `${who} (${jobParts.join(", ")})` : who;
}

/** "Came from Tharstern: 6 jobs, last job 738048" (or "stock code X" when it has no jobs). */
export function cameFromTharsternText(
  provenance: CatalogueItemTharsternProvenance,
): string {
  const parts: string[] = [];
  const jobCount = provenance.jobNos.length;
  if (jobCount > 0)
    parts.push(`${jobCount} ${jobCount === 1 ? "job" : "jobs"}`);
  if (provenance.lastJobNo) parts.push(`last job ${provenance.lastJobNo}`);
  if (provenance.stockCode) parts.push(`stock code ${provenance.stockCode}`);
  return parts.length > 0
    ? `Came from Tharstern: ${parts.join(", ")}`
    : "Came from Tharstern";
}

/**
 * The staff item page's line under the title: "Harbour Dental Group catalogue ·
 * your ref PL-DL6 · Last time: bought from Solopress (job 738048, 2 Sep 2026)".
 * `null` when the item has neither a reference nor a route hint, so a plain
 * item shows nothing extra.
 */
export function migratedItemLine(input: {
  catalogueName: string;
  customerProductRef?: string;
  routeHint?: CatalogueItemRouteHint;
}): string | null {
  if (!input.customerProductRef && !input.routeHint) return null;
  const parts = [
    /catalogue/i.test(input.catalogueName)
      ? input.catalogueName
      : `${input.catalogueName} catalogue`,
    input.customerProductRef ? `your ref ${input.customerProductRef}` : null,
    input.routeHint ? `Last time: ${lastTimeText(input.routeHint)}` : null,
  ].filter((part): part is string => part !== null);
  return parts.join(" · ");
}
